import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { type EhrWriteResponse, useWritebacks } from '@ctw/shared/context/writeback-provider';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { pickBy } from 'lodash-es';
import { useCallback } from 'react';

export function useVitalWriteback() {
  const { requestContext, ctwFetch } = useCTW();
  const { onWritebackSuccess } = useWritebacks();
  const { env } = requestContext;
  const { patientID, systemURL } = usePatientContext();
  if (!(patientID && systemURL)) {
    throw new Error('Patient ID or system URL not found');
  }
  const vitalWritebackRequest = useCallback(
    async (vitalBucket: VitalsBucket): Promise<EhrWriteResponse> => {
      const { builderId } = requestContext;
      if (!builderId) {
        throw new Error('Builder ID not found');
      }

      const body = {
        data: {
          type: 'vitals',
          attributes: pickBy({
            ehrContext: {
              ah_department: requestContext.ehrContext?.ah_department,
            },
            date: vitalBucket.date,
            systolic: vitalBucket.systolic?.observation.valueWithoutUnits,
            diastolic: vitalBucket.diastolic?.observation.valueWithoutUnits,
            temperature: vitalBucket.temperature?.observation.valueWithoutUnits,
            temperatureUnit: vitalBucket.temperature?.observation.unit,
            pulse: vitalBucket.pulse?.observation.valueWithoutUnits,
            respiratoryRate: vitalBucket.respiratoryRate?.observation.valueWithoutUnits,
            pain: vitalBucket.pain?.observation.valueWithoutUnits,
            o2Saturation: vitalBucket.oxygenSaturation?.observation.valueWithoutUnits,
            bmi: vitalBucket.bmi?.observation.valueWithoutUnits,
            height: vitalBucket.height?.observation.valueWithoutUnits,
            heightUnit: vitalBucket.height?.observation.unit,
            weight: vitalBucket.weight?.observation.valueWithoutUnits,
            weightUnit: vitalBucket.weight?.observation.unit,
          }),
          relationships: {
            patient: {
              data: {
                type: 'fhir/Patient',
                identifierSystemURL: systemURL,
                identifierValue: patientID,
              },
            },
          },
        },
      };

      const url = `${getApiServiceBaseUrl({ service: 'ehr-hooks', env })}/vitals`;

      const response = await ctwFetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
          'Content-Type': 'application/json',
          'Zus-Account': builderId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to write vitals to EHR.');
      }

      const writebackResponse = response.data as EhrWriteResponse;
      onWritebackSuccess(vitalBucket.key, writebackResponse);
      return writebackResponse;
    },
    [env, requestContext, ctwFetch, onWritebackSuccess, patientID, systemURL],
  );

  return vitalWritebackRequest;
}
